import { readContract } from "@wagmi/core"
import { BinaryEligibilityOracleEarningPowerCalculator__factory as oracleFactory } from "@/contracts/bindings/factories/BinaryEligibilityOracleEarningPowerCalculator__factory"
import { BaseDRSProvider } from "@/governance/helpers/drs/providers/BaseDRSProvider"
import {
  type DRSConfig,
  type DRSInfo,
  DRSStatus,
  type KarmaApiResponse,
} from "@/governance/types/drs"
import { getChainIdParams } from "@/web3/helpers/chainId"

type KarmaResponseWrapper = {
  responseTime: number
  data: {
    delegates: KarmaApiResponse[]
  }
}

// API Configuration
export const CONFIG = {
  KARMA_BASE_URL: "https://api.karmahq.xyz/api/dao/delegates",
  KARMA_PERIOD: "90d",
  KARMA_PAGE_SIZE: 20,
  MAX_BULK_ADDRESSES: 100,
} as const

export class KarmaDRSProvider extends BaseDRSProvider {
  private period = CONFIG.KARMA_PERIOD
  private baseAPIURL = CONFIG.KARMA_BASE_URL

  constructor(config: DRSConfig) {
    super(config)
  }

  protected async fetchSingleAddress(address: string): Promise<DRSInfo | null> {
    const requestUrl = this.buildRequestUrl([address])

    const response = await fetch(requestUrl)
    if (!response.ok) {
      throw new Error(
        `Failed to fetch Karma data: ${response.status} ${response.statusText}`,
      )
    }

    const data: KarmaResponseWrapper = await response.json()

    if (data.data.delegates.length === 0) {
      return this.createEmptyDRSInfo(address)
    }

    const userData = data.data.delegates.find(
      (delegate) =>
        delegate.publicAddress.toLowerCase() === address.toLowerCase(),
    )

    if (!userData) {
      return this.createEmptyDRSInfo(address)
    }

    return this.transformToDRSInfo(userData)
  }

  protected async fetchBulkAddresses(
    addresses: string[],
  ): Promise<Map<string, DRSInfo>> {
    const result = new Map<string, DRSInfo>()
    const requestUrl = this.buildRequestUrl(addresses)

    const response = await fetch(requestUrl)
    if (!response.ok) {
      throw new Error(
        `Failed to fetch Karma bulk data: ${response.status} ${response.statusText}`,
      )
    }

    const data: KarmaResponseWrapper = await response.json()

    const delegateMap = new Map(
      data.data.delegates.map((d) => [d.publicAddress.toLowerCase(), d]),
    )

    for (const address of addresses) {
      const normalizedAddress = address.toLowerCase()
      const delegateData = delegateMap.get(normalizedAddress)

      if (delegateData) {
        const drsInfo = this.transformToDRSInfo(delegateData)
        result.set(normalizedAddress, drsInfo)
      } else {
        result.set(normalizedAddress, this.createEmptyDRSInfo(address))
      }
    }

    return result
  }

  async initialize(): Promise<this> {
    await this.updateThreshold()
    return this
  }

  protected getProbationSupport(): boolean {
    return false // Karma does not support probation
  }

  private async updateThreshold() {
    const oracleAddress = this.config.delegatesEligibilityOracleAddress
    if (!oracleAddress) return this.config.defaultThreshold

    try {
      const chainReference = this.config.chainId
        ? getChainIdParams(this.config.chainId).reference
        : undefined

      const thresholdScore = await readContract({
        address: oracleAddress,
        abi: oracleFactory.abi,
        functionName: "delegateeEligibilityThresholdScore",
        chainId: chainReference,
      })
      const newThreshold = Number(thresholdScore)
      if (!Number.isNaN(newThreshold)) {
        this.config.defaultThreshold = newThreshold
      }
    } catch (error) {
      this.logError("updateThreshold", error, {
        oracleAddress,
        chainId: this.config.chainId,
      })
      // Continue with default threshold
    }
  }

  private buildRequestUrl(addresses: string[]): string {
    const addressesString = addresses.join(",")
    return `${this.baseAPIURL}?name=${this.config.name}&pageSize=${CONFIG.KARMA_PAGE_SIZE}&offset=0&period=${this.period}&order=desc&field=score&statuses=active,inactive,withdrawn,recognized&addresses=${addressesString}`
  }

  private transformToDRSInfo(karmaData: KarmaApiResponse): DRSInfo {
    const stats = karmaData.stats[0]
    const karmaScore = stats?.karmaScore || 0
    const status = this.determineStatus(karmaData)
    const lastUpdated = stats?.updatedAt || null

    return {
      address: karmaData.publicAddress.toLowerCase(),
      status,
      drsScore: karmaScore,
      lastUpdated,
      externalLink: this.buildExternalLink(karmaData.publicAddress),
      provider: this.config.provider,
      isEligible: status === DRSStatus.ACTIVE,
      hasInfo: true,
      probationSupported: this.getProbationSupport(),
      isProbation: false,
      username: null,
    }
  }

  private determineStatus(karmaData: KarmaApiResponse): DRSStatus {
    const stats = karmaData.stats[0]
    if (!stats) return DRSStatus.INACTIVE

    const onChainVotesPct = stats.onChainVotesPct || 0
    const offChainVotesPct = stats.offChainVotesPct || 0

    // Ghost: has voting power but has never voted
    if (onChainVotesPct === 0 && offChainVotesPct === 0) {
      return DRSStatus.GHOST
    }

    // Active: >=thresholdScore% DRS over last 5 proposals
    // Using karmaScore as DRS (Delegate Reputation Score)
    const karmaScore = stats.karmaScore || 0
    if (karmaScore >= this.config.defaultThreshold) {
      return DRSStatus.ACTIVE
    }

    // Inactive: <thresholdScore% DRS
    return DRSStatus.INACTIVE
  }
}
